// src/realtime/commentsSocket.ts
// ==========================================================
//    Comments Realtime Socket
// ==========================================================

import websocketManager from "../lib/websocketManager";
import { logger } from "../utils/logging/logger";

type CommentEventName =
  | "created"
  | "updated"
  | "deleted"
  | "subscribed"
  | "unsubscribed";

type OnEvent = (evt: { event: CommentEventName; data: any }) => void;

// -----------------------------------------------------------------------------
// Ensure websocket connection
// -----------------------------------------------------------------------------
export function ensureCommentsSocketConnected(token: string) {
  try {
    websocketManager.connect(token, "comments");
  } catch (err) {
    logger.error("[commentsSocket] Failed to connect WS", err);
  }
}

// -----------------------------------------------------------------------------
// Subscribe to one comment thread
// -----------------------------------------------------------------------------
export function subscribeCommentsThread(ctId: number, objectId: number) {
  if (!websocketManager.isConnected()) {
    logger.warn("[commentsSocket] WS not connected; cannot subscribe");
    return;
  }

  websocketManager.sendMessage("comments", {
    type: "subscribe",
    data: {
      content_type_id: ctId,
      object_id: objectId,
    },
  });
}

// -----------------------------------------------------------------------------
// Unsubscribe from one comment thread
// -----------------------------------------------------------------------------
export function unsubscribeCommentsThread(ctId: number, objectId: number) {
  if (!websocketManager.isConnected()) return;

  websocketManager.sendMessage("comments", {
    type: "unsubscribe",
    data: {
      content_type_id: ctId,
      object_id: objectId,
    },
  });
}

// -----------------------------------------------------------------------------
// Listen to comment events
// -----------------------------------------------------------------------------
export function onCommentEvent(handler: OnEvent) {
  const makeCb =
    (event: CommentEventName) =>
    (data: any) => {
      handler({ event, data });
    };

  const cbCreated = makeCb("created");
  const cbUpdated = makeCb("updated");
  const cbDeleted = makeCb("deleted");
  const cbSubscribed = makeCb("subscribed");
  const cbUnsubscribed = makeCb("unsubscribed");

  websocketManager.on("comments:created", cbCreated);
  websocketManager.on("comments:updated", cbUpdated);
  websocketManager.on("comments:deleted", cbDeleted);
  websocketManager.on("comments:subscribed", cbSubscribed);
  websocketManager.on("comments:unsubscribed", cbUnsubscribed);

  return () => {
    websocketManager.off("comments:created", cbCreated);
    websocketManager.off("comments:updated", cbUpdated);
    websocketManager.off("comments:deleted", cbDeleted);
    websocketManager.off("comments:subscribed", cbSubscribed);
    websocketManager.off("comments:unsubscribed", cbUnsubscribed);
  };
}

// -----------------------------------------------------------------------------
// Optional listener for comments error event
// -----------------------------------------------------------------------------
export function onCommentsError(handler: (data: any) => void) {
  const cb = (data: any) => handler(data);
  websocketManager.on("comments:error", cb);

  return () => {
    websocketManager.off("comments:error", cb);
  };
}